<template>
  <div class="data-overview">
    <!-- 顶部统计栏 -->
    <div class="top-stats">
      <div class="stat-item">
        <span class="label">数据更新时间：</span>
        <span class="value">{{ updateTime }}</span>
      </div>
      <div class="stat-item">
        <span class="label">数据覆盖：</span>
        <span class="value">{{ statsData.roadCount }}个路段，{{ statsData.spaceCount }}个泊位</span>
      </div>
      <div class="stat-item">
        <span class="label">异常统计：</span>
        <span class="value">本月地磁异常{{ statsData.abnormalCount }}例（已处理）</span>
      </div>
      <div class="stat-item">
        <span class="label">投诉统计：</span>
        <span class="value">{{ statsData.complaintCount }}例12345投诉（已闭环）</span>
      </div>
    </div>
    
    <!-- 智能预警 -->
    <div class="top-stats warning-stats">
      <div class="warning-header">
        <span class="label">智能预警：</span>
        <el-button 
          type="text" 
          size="mini"
          icon="el-icon-setting"
          @click="showWarningSettings = true"
          class="warning-settings-btn"
        >设置阈值</el-button>
      </div>
      <div class="warning-list-container">
        <div v-if="warnings.length === 0" class="no-warning">暂无预警</div>
        <div 
          v-for="(warning, index) in warnings" 
          :key="index"
          class="warning-row"
        >
          <span class="warning-tag" :class="warning.type">
            <span class="warning-icon">{{ warning.icon }}</span>
            <span class="warning-text">{{ warning.text }}</span>
          </span>
        </div>
      </div>
    </div>
    
    <!-- 预警阈值设置对话框 -->
    <el-dialog
      title="预警阈值设置"
      :visible.sync="showWarningSettings"
      width="500px"
      :close-on-click-modal="false"
    >
      <div class="warning-threshold-form">
        <div class="threshold-form-item">
          <label>低收费率阈值：</label>
          <el-input-number 
            v-model="warningThresholds.lowRate" 
            :min="0" 
            :max="100" 
            :precision="2"
            size="small"
            style="width: 150px;"
          ></el-input-number>
          <span class="unit">%</span>
          <span class="desc">低于此值将预警</span>
        </div>
        <div class="threshold-form-item">
          <label>低周转率阈值：</label>
          <el-input-number 
            v-model="warningThresholds.lowTurnover" 
            :min="0" 
            :precision="2"
            size="small"
            style="width: 150px;"
          ></el-input-number>
          <span class="unit">次/天</span>
          <span class="desc">低于此值将预警</span>
        </div>
        <div class="threshold-form-item">
          <label>高欠费率阈值：</label>
          <el-input-number 
            v-model="warningThresholds.highArrears" 
            :min="0" 
            :max="100" 
            :precision="2"
            size="small"
            style="width: 150px;"
          ></el-input-number>
          <span class="unit">%</span>
          <span class="desc">高于此值将预警</span>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="showWarningSettings = false">取消</el-button>
        <el-button type="primary" @click="saveWarningThresholds">确认</el-button>
      </div>
    </el-dialog>
    
    <!-- 核心指标卡（顶部区域） -->
    <div class="top-indicators">
      <!-- 全局指标卡 -->
      <div class="indicator-cards">
        <div class="indicator-card">
          <div class="card-header">
            <span class="card-title">收入总览</span>
            <span class="card-period">本月</span>
          </div>
          <div class="card-content income-overview">
            <div class="indicator-item">
              <span class="label">应收总额</span>
              <div class="value-wrapper">
                <span class="value">{{ formatMoney(overviewData.receivableTotal) }}</span>
                <span class="trend" :class="overviewData.receivableTrend > 0 ? 'up' : 'down'">
                  {{ overviewData.receivableTrend > 0 ? '↑' : '↓' }}{{ Math.abs(overviewData.receivableTrend) }}%
                </span>
              </div>
            </div>
            <div class="indicator-item">
              <span class="label">订单总额</span>
              <div class="value-wrapper">
                <span class="value">{{ formatMoney(overviewData.orderTotal) }}</span>
                <span class="trend" :class="overviewData.orderTrend > 0 ? 'up' : 'down'">
                  {{ overviewData.orderTrend > 0 ? '↑' : '↓' }}{{ Math.abs(overviewData.orderTrend) }}%
                </span>
              </div>
            </div>
            <div class="indicator-item">
              <span class="label">实收总额</span>
              <div class="value-wrapper">
                <span class="value">{{ formatMoney(overviewData.realTotal) }}</span>
                <span class="trend" :class="overviewData.realTrend > 0 ? 'up' : 'down'">
                  {{ overviewData.realTrend > 0 ? '↑' : '↓' }}{{ Math.abs(overviewData.realTrend) }}%
                </span>
              </div>
            </div>
            <div class="indicator-item">
              <span class="label">下单率</span>
              <span class="value">{{ overviewData.orderRate }}%</span>
            </div>
            <div class="indicator-item">
              <span class="label">收费率</span>
              <span class="value">{{ overviewData.chargeRate }}%</span>
            </div>
          </div>
        </div>
        
        <div class="indicator-card">
          <div class="card-header">
            <span class="card-title">效率总览</span>
            <span class="card-period">本月</span>
          </div>
          <div class="card-content efficiency-overview">
            <div class="indicator-item">
              <span class="label">日均泊位收益</span>
              <span class="value">{{ formatMoney(overviewData.dailySpaceRevenue) }}</span>
              <span class="unit">元/天</span>
            </div>
            <div class="indicator-item">
              <span class="label">日均人效</span>
              <span class="value">{{ formatMoney(overviewData.dailyPersonEfficiency) }}</span>
              <span class="unit">元/天</span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 趋势指标卡 -->
      <div class="trend-cards">
            <div class="trend-card">
          <div class="trend-header">
            <span>下单率趋势</span>
            <span class="current-value">{{ overviewData.orderRate }}%</span>
          </div>
          <div id="orderRateChart" class="trend-chart"></div>
        </div>
        <div class="trend-card">
          <div class="trend-header">
            <span>收费率趋势</span>
            <span class="current-value">{{ overviewData.chargeRate }}%</span>
          </div>
          <div id="chargeRateChart" class="trend-chart"></div>
        </div>
        
    
        
        <div class="trend-card">
          <div class="trend-header">
            <span>泊位周转率均值</span>
            <span class="current-value">{{ overviewData.turnoverRate }}次/天</span>
          </div>
          <div id="turnoverRateChart" class="trend-chart"></div>
        </div>
      </div>
    </div>
    
    <!-- 数据看板主区域 -->
    <div class="dashboard-main">
      <!-- 第一行：三个热力图模块 -->
      <div class="dashboard-row">
        <!-- 收入分布热力图 -->
        <div class="dashboard-quadrant quadrant-1">
          <div class="quadrant-header">
            <h3>收入分布热力图</h3>
            <div class="heatmap-controls">
              <el-radio-group v-model="heatmapDimension" size="small" @change="onDimensionChange">
                <el-radio-button label="real">实收</el-radio-button>
                <el-radio-button label="receivable">应收</el-radio-button>
                <el-radio-button label="rate">收费率</el-radio-button>
              </el-radio-group>
            </div>
          </div>
          <!-- 阈值设置 -->
          <div class="threshold-controls-header">
            <div class="threshold-item">
              <label>绿色(≥)</label>
              <el-input 
                v-model="greenThreshold" 
                size="small"
                style="width: 80px;"
                @keyup.enter="updateHeatmap"
              ></el-input>
              <span class="threshold-unit">{{ heatmapDimension === 'rate' ? '%' : '元' }}</span>
            </div>
            <div class="threshold-item">
              <label>红色(≤)</label>
              <el-input 
                v-model="redThreshold" 
                size="small"
                style="width: 80px;"
                @keyup.enter="updateHeatmap"
              ></el-input>
              <span class="threshold-unit">{{ heatmapDimension === 'rate' ? '%' : '元' }}</span>
            </div>
            <el-button 
              type="primary" 
              size="small" 
              @click="updateHeatmap"
              class="confirm-btn"
            >
              确认
            </el-button>
          </div>
          <!-- 地图图例说明 -->
          <div class="heatmap-legend-header">
            <div class="legend-title">阈值说明：</div>
            <div class="legend-items">
              <div class="legend-item">
                <img :src="createDropIcon('#67C23A', 20)" alt="绿色" class="legend-icon" />
                <span class="legend-label">≥ {{ greenThreshold || '--' }}{{ heatmapDimension === 'rate' ? '%' : '元' }}</span>
              </div>
              <div class="legend-item">
                <img :src="createDropIcon('#E6A23C', 20)" alt="其它" class="legend-icon" />
                <span class="legend-label">其它</span>
              </div>
              <div class="legend-item">
                <img :src="createDropIcon('#F56C6C', 20)" alt="红色" class="legend-icon" />
                <span class="legend-label">≤ {{ redThreshold || '--' }}{{ heatmapDimension === 'rate' ? '%' : '元' }}</span>
              </div>
            </div>
          </div>
          <div id="heatmapMap" class="heatmap-container">
            <!-- 排名浮动图层 -->
            <div class="ranking-floating-panel" v-if="showRankingPanel">
              <div class="panel-header">
                <span class="panel-title">排名榜单</span>
                <el-button 
                  type="text" 
                  icon="el-icon-close" 
                  size="mini"
                  @click="showRankingPanel = false"
                  class="close-btn"
                ></el-button>
              </div>
              <div class="panel-content">
                <div class="ranking-section">
                  <div class="section-title">前5名</div>
                  <div class="ranking-list">
                    <div 
                      v-for="(item, index) in topRanking" 
                      :key="index"
                      class="ranking-item top-item"
                    >
                      <span class="rank-number">{{ index + 1 }}</span>
                      <span class="road-name">{{ item.name }}</span>
                      <span class="road-value">{{ formatRankingValue(item.value) }}</span>
                    </div>
                  </div>
                </div>
                <div class="ranking-section">
                  <div class="section-title">后5名</div>
                  <div class="ranking-list">
                    <div 
                      v-for="(item, index) in bottomRanking" 
                      :key="index"
                      class="ranking-item bottom-item"
                    >
                      <span class="rank-number">{{ totalCount - 4 + index }}</span>
                      <span class="road-name">{{ item.name }}</span>
                      <span class="road-value">{{ formatRankingValue(item.value) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- 显示面板按钮（面板隐藏时显示） -->
            <div class="show-panel-btn" v-if="!showRankingPanel" @click="showRankingPanel = true">
              <i class="el-icon-menu"></i>
              <span>显示榜单</span>
            </div>
          </div>
        </div>
        
        <!-- 泊位周转热力图 -->
        <div class="dashboard-quadrant quadrant-2">
          <div class="quadrant-header">
            <h3>泊位周转热力图</h3>
            <div class="heatmap-controls">
              <el-radio-group v-model="activeRankingTab" size="small" @change="onRankingTabChange">
                <el-radio-button label="spaceProductivity">泊位单产</el-radio-button>
                <el-radio-button label="turnover">泊位周转率</el-radio-button>
              </el-radio-group>
            </div>
          </div>
          <!-- 阈值设置 -->
          <div class="threshold-controls-header">
            <div class="threshold-item">
              <label>绿色(≥)</label>
              <el-input 
                v-model="rankingGreenThreshold" 
                size="small"
                style="width: 80px;"
                @keyup.enter="updateRankingHeatmap"
              ></el-input>
              <span class="threshold-unit">{{ activeRankingTab === 'turnover' ? '次' : '元' }}</span>
            </div>
            <div class="threshold-item">
              <label>红色(≤)</label>
              <el-input 
                v-model="rankingRedThreshold" 
                size="small"
                style="width: 80px;"
                @keyup.enter="updateRankingHeatmap"
              ></el-input>
              <span class="threshold-unit">{{ activeRankingTab === 'turnover' ? '次' : '元' }}</span>
            </div>
            <el-button 
              type="primary" 
              size="small" 
              @click="updateRankingHeatmap"
              class="confirm-btn"
            >
              确认
            </el-button>
          </div>
          <!-- 地图图例说明 -->
          <div class="heatmap-legend-header">
            <div class="legend-title">阈值说明：</div>
            <div class="legend-items">
              <div class="legend-item">
                <img :src="createDropIcon('#67C23A', 20)" alt="绿色" class="legend-icon" />
                <span class="legend-label">≥ {{ rankingGreenThreshold || '--' }}{{ activeRankingTab === 'turnover' ? '次' : '元' }}</span>
              </div>
              <div class="legend-item">
                <img :src="createDropIcon('#E6A23C', 20)" alt="其它" class="legend-icon" />
                <span class="legend-label">其它</span>
              </div>
              <div class="legend-item">
                <img :src="createDropIcon('#F56C6C', 20)" alt="红色" class="legend-icon" />
                <span class="legend-label">≤ {{ rankingRedThreshold || '--' }}{{ activeRankingTab === 'turnover' ? '次' : '元' }}</span>
              </div>
            </div>
          </div>
          <div id="rankingMap" class="heatmap-container">
            <!-- 排名浮动图层 -->
            <div class="ranking-floating-panel" v-if="showRankingPanel2">
              <div class="panel-header">
                <span class="panel-title">排名榜单</span>
                <el-button 
                  type="text" 
                  icon="el-icon-close" 
                  size="mini"
                  @click="showRankingPanel2 = false"
                  class="close-btn"
                ></el-button>
              </div>
              <div class="panel-content">
                <div class="ranking-section">
                  <div class="section-title">前5名</div>
                  <div class="ranking-list">
                    <div 
                      v-for="(item, index) in topRanking2" 
                      :key="index"
                      class="ranking-item top-item"
                    >
                      <span class="rank-number">{{ index + 1 }}</span>
                      <span class="road-name">{{ item.name }}</span>
                      <span class="road-value">{{ formatRankingValue2(item.value) }}</span>
                    </div>
                  </div>
                </div>
                <div class="ranking-section">
                  <div class="section-title">后5名</div>
                  <div class="ranking-list">
                    <div 
                      v-for="(item, index) in bottomRanking2" 
                      :key="index"
                      class="ranking-item bottom-item"
                    >
                      <span class="rank-number">{{ totalCount2 - 4 + index }}</span>
                      <span class="road-name">{{ item.name }}</span>
                      <span class="road-value">{{ formatRankingValue2(item.value) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- 显示面板按钮（面板隐藏时显示） -->
            <div class="show-panel-btn" v-if="!showRankingPanel2" @click="showRankingPanel2 = true">
              <i class="el-icon-menu"></i>
              <span>显示榜单</span>
            </div>
          </div>
        </div>
        
        <!-- 收费员绩效 -->
        <div class="dashboard-quadrant quadrant-4">
          <div class="quadrant-header">
            <h3>收费员绩效</h3>
          </div>
          <div class="performance-section">
            <el-table 
              :data="chargeUserPerformanceList" 
              stripe
              size="small"
              class="charge-user-table"
              :max-height="600"
            >
              <el-table-column prop="name" label="收费员" width="100" fixed="left">
                <template slot-scope="scope">
                  <span class="user-name">{{ scope.row.name }}</span>
                </template>
              </el-table-column>
              <el-table-column 
                label="打单率" 
                min-width="140"
                prop="orderRate"
                sortable
                :sort-method="(a, b) => a.orderRate - b.orderRate"
              >
                <template slot-scope="scope">
                  <div class="performance-cell">
                    <span class="main-value">{{ scope.row.orderCount }}</span>
                    <span class="unit">单</span>
                    <span class="percent">({{ scope.row.orderRate }}%)</span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column 
                label="收费率" 
                min-width="160"
                prop="chargeRate"
                sortable
                :sort-method="(a, b) => a.chargeRate - b.chargeRate"
              >
                <template slot-scope="scope">
                  <div class="performance-cell">
                    <span class="main-value">{{ formatMoney(scope.row.realTotal) }}</span>
                    <span class="unit">元</span>
                    <span class="percent">({{ scope.row.chargeRate }}%)</span>
                  </div>
                </template>
              </el-table-column>
              <!-- <el-table-column 
                label="欠费追缴率" 
                min-width="160"
                prop="recoveryRate"
                sortable
                :sort-method="(a, b) => a.recoveryRate - b.recoveryRate"
              >
                <template slot-scope="scope">
                  <div class="performance-cell">
                    <span class="main-value">{{ formatMoney(scope.row.recoveryAmount) }}</span>
                    <span class="unit">元</span>
                    <span class="percent">({{ scope.row.recoveryRate }}%)</span>
                  </div>
                </template>
              </el-table-column> -->
            </el-table>
          </div>
        </div>
      </div>
      
      <!-- 收入对比分析 -->
      <div class="dashboard-row">
        <div class="dashboard-quadrant quadrant-3">
          <div class="quadrant-header">
            <h3>收入对比分析</h3>
          </div>
          <div id="comparisonChart" class="comparison-chart"></div>
          <div class="top3-container">
            <div class="growth-top3">
              <h4>路段增长TOP3（应收比）</h4>
              <div class="growth-list">
                <div 
                  v-for="(item, index) in growthTop3" 
                  :key="index"
                  class="growth-item"
                >
                  <span class="name">{{ item.roadName }}</span>
                  <span class="ratio">应收比{{ item.ratio }}</span>
                  <span class="trend-icon">📈</span>
                </div>
              </div>
            </div>
            <div class="growth-top3">
              <h4>路段增长TOP3（实收比）</h4>
              <div class="growth-list">
                <div 
                  v-for="(item, index) in realGrowthTop3" 
                  :key="index"
                  class="growth-item"
                >
                  <span class="name">{{ item.roadName }}</span>
                  <span class="ratio">实收比{{ item.ratio }}</span>
                  <span class="trend-icon">📈</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// 导入天地图工具函数
import {
  waitForTiandituAPI,
  createTiandituMap,
  setMapEvent,
  addMapControl,
  createMarker,
  addMarkerToMap,
  clearAllMarkers,
  setMapCenter
} from '@/utils/tianditu'

export default {
  name: 'DataOverview',
  data() {
    return {
      heatmapDimension: 'real',
      showWarningSettings: false, // 显示预警阈值设置对话框
      warningThresholds: {
        lowRate: 15, // 低收费率阈值（%）
        lowTurnover: 1.5, // 低周转率阈值（次/天）
        highArrears: 70 // 高欠费率阈值（%）
      },
      activeRankingTab: 'spaceProductivity',
      greenThreshold: '8000', // 绿色阈值：大于等于某个金额值（实收默认值）
      redThreshold: '3000', // 红色阈值：小于等于某个金额值（实收默认值）
      showRankingPanel: true, // 显示排名浮动面板
      topRanking: [], // 前5名数据
      bottomRanking: [], // 后5名数据
      totalCount: 0, // 总数量
      // 泊位指标排行榜相关
      rankingGreenThreshold: '300', // 绿色阈值（泊位单产默认值）
      rankingRedThreshold: '100', // 红色阈值（泊位单产默认值）
      showRankingPanel2: true, // 显示排名浮动面板2
      topRanking2: [], // 前5名数据2
      bottomRanking2: [], // 后5名数据2
      totalCount2: 0, // 总数量2
      rankingMap: null, // 排行榜地图实例
      
      // 核心指标数据
      overviewData: {
        operationDays: 30,
        totalSpaces: 883,
        receivableTotal: 298456,
        realTotal: 61432,
        chargeRate: 20.58,
        receivableTrend: 10.2,
        realTrend: 8.5,
        orderTotal: 125680,
        orderTrend: 12.5,
        orderRate: 85.6,
        dailySpaceRevenue: 69.57,
        dailyPersonEfficiency: 2047.73,
        realRate: 20.58,
        turnoverRate: 2.15
      },
      
      // 泊位指标排行榜数据
      spaceProductivityRanking: [
        { roadName: '锦程路', receivablePerSpace: 561.2, realPerSpace: 103.8, spaceCount: 88 },
        { roadName: '智达路', receivablePerSpace: 437.4, realPerSpace: 105.6, spaceCount: 80 },
        { roadName: '清源北路', receivablePerSpace: 356.8, realPerSpace: 87.8, spaceCount: 80 },
        { roadName: '镇江东路二段', receivablePerSpace: 334.9, realPerSpace: 81.8, spaceCount: 80 },
        { roadName: '锦亭路', receivablePerSpace: 493.0, realPerSpace: 48.7, spaceCount: 80 },
        { roadName: '大兴街', receivablePerSpace: 269.3, realPerSpace: 121.7, spaceCount: 43 },
        { roadName: '清源南路', receivablePerSpace: 248.5, realPerSpace: 48.6, spaceCount: 80 },
        { roadName: '锦华路', receivablePerSpace: 234.6, realPerSpace: 43.2, spaceCount: 80 },
        { roadName: '智达南路', receivablePerSpace: 279.3, realPerSpace: 51.5, spaceCount: 80 },
        { roadName: '清源中路', receivablePerSpace: 251.5, realPerSpace: 47.4, spaceCount: 80 }
      ],
      
      turnoverRanking: [
        { roadName: '大兴街', turnoverRate: 4.05 },
        { roadName: '智达路', turnoverRate: 3.25 },
        { roadName: '清源北路', turnoverRate: 3.12 },
        { roadName: '镇江东路二段', turnoverRate: 2.98 },
        { roadName: '锦程路', turnoverRate: 2.85 },
        { roadName: '锦华路', turnoverRate: 2.65 },
        { roadName: '智达南路', turnoverRate: 2.45 },
        { roadName: '清源中路', turnoverRate: 2.32 },
        { roadName: '锦程南路', turnoverRate: 2.18 },
        { roadName: '智达北路', turnoverRate: 2.05 },
        { roadName: '锦亭路', turnoverRate: 1.85 },
        { roadName: '清源南路', turnoverRate: 1.65 },
        { roadName: '潆华西路', turnoverRate: 0.84 },
        { roadName: '潆华东路', turnoverRate: 0.71 }
      ],
      
      // 增长TOP3
      growthTop3: [
        { roadName: '镇江东路二段', ratio: 1.88 },
        { roadName: '镇江东路一段', ratio: 1.78 },
        { roadName: '智达路', ratio: 1.32 }
      ],
      
      // 实收比TOP3
      realGrowthTop3: [
        { roadName: '清源北路', ratio: 1.95 },
        { roadName: '智达路', ratio: 1.82 },
        { roadName: '镇江东路二段', ratio: 1.65 }
      ],
      
      // 绩效数据
      // 收费员绩效数据（分收费员统计）
      chargeUserPerformanceList: [
        {
          name: '殷保生',
          orderCount: 320,
          orderRate: 88.5,
          realTotal: 63010,
          chargeRate: 82.3,
          recoveryAmount: 5230,
          recoveryRate: 68.5
        },
        {
          name: '赵小英',
          orderCount: 285,
          orderRate: 85.2,
          realTotal: 55120,
          chargeRate: 79.6,
          recoveryAmount: 4120,
          recoveryRate: 65.8
        },
        {
          name: '青全华',
          orderCount: 268,
          orderRate: 83.1,
          realTotal: 47610,
          chargeRate: 76.8,
          recoveryAmount: 3890,
          recoveryRate: 62.3
        },
        {
          name: '杨英',
          orderCount: 245,
          orderRate: 72.5,
          realTotal: 31240,
          chargeRate: 65.2,
          recoveryAmount: 2150,
          recoveryRate: 58.7
        },
        {
          name: '张明',
          orderCount: 298,
          orderRate: 86.3,
          realTotal: 48920,
          chargeRate: 80.1,
          recoveryAmount: 4560,
          recoveryRate: 70.2
        },
        {
          name: '王强',
          orderCount: 312,
          orderRate: 87.8,
          realTotal: 52180,
          chargeRate: 81.5,
          recoveryAmount: 5120,
          recoveryRate: 72.1
        },
        {
          name: '李华',
          orderCount: 275,
          orderRate: 84.6,
          realTotal: 45230,
          chargeRate: 77.8,
          recoveryAmount: 3650,
          recoveryRate: 63.5
        },
        {
          name: '陈静',
          orderCount: 290,
          orderRate: 86.1,
          realTotal: 49850,
          chargeRate: 79.2,
          recoveryAmount: 4280,
          recoveryRate: 67.8
        },
        {
          name: '刘伟',
          orderCount: 265,
          orderRate: 82.3,
          realTotal: 43890,
          chargeRate: 75.5,
          recoveryAmount: 3420,
          recoveryRate: 61.2
        },
        {
          name: '周芳',
          orderCount: 280,
          orderRate: 85.7,
          realTotal: 46520,
          chargeRate: 78.3,
          recoveryAmount: 3890,
          recoveryRate: 64.8
        },
        {
          name: '吴刚',
          orderCount: 255,
          orderRate: 81.2,
          realTotal: 42150,
          chargeRate: 74.6,
          recoveryAmount: 3150,
          recoveryRate: 59.5
        },
        {
          name: '孙丽',
          orderCount: 302,
          orderRate: 87.5,
          realTotal: 51230,
          chargeRate: 80.8,
          recoveryAmount: 4780,
          recoveryRate: 71.3
        },
        {
          name: '马强',
          orderCount: 272,
          orderRate: 84.2,
          realTotal: 44560,
          chargeRate: 76.9,
          recoveryAmount: 3520,
          recoveryRate: 62.7
        },
        {
          name: '黄娟',
          orderCount: 288,
          orderRate: 85.9,
          realTotal: 49120,
          chargeRate: 79.5,
          recoveryAmount: 4150,
          recoveryRate: 66.9
        },
        {
          name: '徐磊',
          orderCount: 260,
          orderRate: 82.8,
          realTotal: 43280,
          chargeRate: 75.2,
          recoveryAmount: 3280,
          recoveryRate: 60.8
        }
      ],
      
      // 预警数据（根据阈值动态计算）
      warnings: [],
      
      // 统计数据
      statsData: {
        roadCount: 15,
        spaceCount: 883,
        abnormalCount: 40,
        complaintCount: 8
      },
      
      updateTime: '2025-12-31 23:59',
      
      // 图表实例
      charts: {}
    }
  },
  
  mounted() {
    this.initCharts()
    this.loadData()
    this.initRankingMap()
    this.calculateWarnings()
  },
  
  methods: {
    // 初始化所有图表
    initCharts() {
      this.$nextTick(() => {
        this.initChargeRateChart()
        this.initOrderRateChart()
        this.initTurnoverRateChart()
        this.initHeatmapChart()
        this.initComparisonChart()
      })
    },
    
    // 收费率趋势图
    initChargeRateChart() {
      const chartDom = document.getElementById('chargeRateChart')
      if (!chartDom) return
      
      const chart = this.$echarts.init(chartDom)
      const option = {
        grid: { left: '10%', right: '10%', top: '15%', bottom: '10%' },
        xAxis: {
          type: 'category',
          data: ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'],
          axisLabel: { fontSize: 10 }
        },
        yAxis: {
          type: 'value',
          axisLabel: { formatter: '{value}%', fontSize: 10 }
        },
        series: [{
          type: 'line',
          data: [18.5, 19.2, 20.1, 19.8, 20.5, 21.2, 20.8, 21.5, 20.3, 19.9, 20.2, 20.58],
          smooth: true,
          lineStyle: { color: '#409EFF' },
          areaStyle: { color: 'rgba(64, 158, 255, 0.1)' }
        }]
      }
      chart.setOption(option)
      this.charts.chargeRate = chart
      
      window.addEventListener('resize', () => chart.resize())
    },
    
    // 下单率趋势图
    initOrderRateChart() {
      const chartDom = document.getElementById('orderRateChart')
      if (!chartDom) return
      
      const chart = this.$echarts.init(chartDom)
      const option = {
        grid: { left: '10%', right: '10%', top: '15%', bottom: '10%' },
        xAxis: {
          type: 'category',
          data: ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'],
          axisLabel: { fontSize: 10 }
        },
        yAxis: {
          type: 'value',
          axisLabel: { formatter: '{value}%', fontSize: 10 }
        },
        series: [{
          type: 'bar',
          data: [82.5, 83.2, 84.1, 84.8, 85.5, 85.2, 85.8, 86.5, 85.3, 85.9, 85.2, 85.6],
          itemStyle: { color: '#67C23A' }
        }]
      }
      chart.setOption(option)
      this.charts.orderRate = chart
      
      window.addEventListener('resize', () => chart.resize())
    },
    
    // 周转率趋势图
    initTurnoverRateChart() {
      const chartDom = document.getElementById('turnoverRateChart')
      if (!chartDom) return
      
      const chart = this.$echarts.init(chartDom)
      const option = {
        grid: { left: '10%', right: '10%', top: '15%', bottom: '10%' },
        xAxis: {
          type: 'category',
          data: ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'],
          axisLabel: { fontSize: 10 }
        },
        yAxis: {
          type: 'value',
          axisLabel: { formatter: '{value}次', fontSize: 10 }
        },
        series: [{
          type: 'line',
          data: [1.8, 1.9, 2.0, 2.1, 2.05, 2.15, 2.2, 2.18, 2.12, 2.08, 2.1, 2.15],
          smooth: true,
          lineStyle: { color: '#E6A23C' }
        }]
      }
      chart.setOption(option)
      this.charts.turnoverRate = chart
      
      window.addEventListener('resize', () => chart.resize())
    },
    
    // 热力图地图
    async initHeatmapChart() {
      const mapContainer = document.getElementById('heatmapMap')
      if (!mapContainer) return
      
      try {
        // 等待天地图API加载完成
        await waitForTiandituAPI()
        
        // 创建天地图实例 - 定位到南充市
        const map = createTiandituMap('heatmapMap', {
          centerLng: 106.110698, // 南充市经度
          centerLat: 30.837793,  // 南充市纬度
          zoom: 13
        })
        
        if (!map) {
          this.$message.error('地图初始化失败，请刷新页面重试')
          return
        }
        
        this.heatmapMap = map
        
        // 设置地图事件
        setMapEvent(map)
        
        // 不添加地图控件（去掉缩放图标和logo）
        // addMapControl(map)
        
        // 初始化标记点
        this.updateHeatmap()
        
        window.addEventListener('resize', () => {
          if (map && typeof map.resize === 'function') {
            map.resize()
          }
        })
      } catch (error) {
        console.error('地图初始化错误:', error)
        this.$message.error('地图初始化失败，请刷新页面重试')
      }
    },
    
    // 维度切换时更新阈值默认值
    onDimensionChange() {
      // 根据维度设置默认阈值
      if (this.heatmapDimension === 'real') {
        this.greenThreshold = '8000'
        this.redThreshold = '3000'
      } else if (this.heatmapDimension === 'receivable') {
        this.greenThreshold = '40000'
        this.redThreshold = '20000'
      } else if (this.heatmapDimension === 'rate') {
        this.greenThreshold = '20'
        this.redThreshold = '12'
      }
      // 更新地图
      this.updateHeatmap()
    },
    
    updateHeatmap() {
      if (!this.heatmapMap) return
      
      const data = this.getHeatmapData()
      
      // 根据选择的维度确定显示的值
      let valueKey = 'real'
      let valueLabel = '实收'
      
      if (this.heatmapDimension === 'receivable') {
        valueKey = 'receivable'
        valueLabel = '应收'
      } else if (this.heatmapDimension === 'rate') {
        valueKey = 'rate'
        valueLabel = '收费率'
      }
      
      // 计算总应收和总实收，用于计算占比
      const totalReceivable = data.reduce((sum, item) => sum + item.receivable, 0)
      const totalReal = data.reduce((sum, item) => sum + item.real, 0)
      
      // 准备数据，添加占比和等级信息
      // 所有维度都使用阈值判断
      const mapData = data.map(item => {
        const value = item[valueKey]
        const realPercent = totalReal > 0 ? ((item.real / totalReal) * 100).toFixed(2) : '0'
        const receivablePercent = totalReceivable > 0 ? ((item.receivable / totalReceivable) * 100).toFixed(2) : '0'
        
        // 根据阈值确定颜色
        // 实收和应收：使用金额阈值（元）
        // 收费率：使用百分比阈值（%）
        let level = 'middle' // middle-橙色
        const greenThresholdNum = parseFloat(this.greenThreshold) || 0
        const redThresholdNum = parseFloat(this.redThreshold) || 0
        
        if (greenThresholdNum > 0 && value >= greenThresholdNum) {
          level = 'top' // top-绿色（大于等于绿色阈值）
        } else if (redThresholdNum > 0 && value <= redThresholdNum) {
          level = 'bottom' // bottom-红色（小于等于红色阈值）
        }
        
        return {
          ...item,
          value,
          realPercent,
          receivablePercent,
          level,
          valueLabel
        }
      })
      
      // 计算排名数据（前5名和后5名）
      const sortedData = [...mapData].sort((a, b) => b.value - a.value)
      this.totalCount = sortedData.length
      this.topRanking = sortedData.slice(0, 5).map(item => ({
        name: item.name,
        value: item.value
      }))
      this.bottomRanking = sortedData.slice(-5).map(item => ({
        name: item.name,
        value: item.value
      }))
      
      // 清除之前的标记点
      clearAllMarkers(this.heatmapMap)
      
      // 统一图标大小
      const iconSize = 24
      const iconHeight = 30
      const iconAnchorX = iconSize / 2
      const iconAnchorY = iconHeight
      
      // 根据等级设置不同的marker样式（统一大小，不同颜色）
      const markerConfigs = {
        top: { // 前5名 - 绿色
          iconUrl: this.createDropIcon('#67C23A', iconSize),
          iconSizeWidth: iconSize,
          iconSizeHeight: iconHeight,
          iconAnchorX: iconAnchorX,
          iconAnchorY: iconAnchorY,
          color: '#67C23A',
          label: '前5名'
        },
        middle: { // 其它 - 橙色
          iconUrl: this.createDropIcon('#E6A23C', iconSize),
          iconSizeWidth: iconSize,
          iconSizeHeight: iconHeight,
          iconAnchorX: iconAnchorX,
          iconAnchorY: iconAnchorY,
          color: '#E6A23C',
          label: '其它'
        },
        bottom: { // 后5名 - 红色
          iconUrl: this.createDropIcon('#F56C6C', iconSize),
          iconSizeWidth: iconSize,
          iconSizeHeight: iconHeight,
          iconAnchorX: iconAnchorX,
          iconAnchorY: iconAnchorY,
          color: '#F56C6C',
          label: '后5名'
        }
      }
      
      // 添加标记点
      let markerCount = 0
      const validMapData = mapData.filter(item => item.lng && item.lat && item.level)
      console.log(`准备添加 ${validMapData.length} 个marker，总数据: ${mapData.length}`)
      
      validMapData.forEach((item, index) => {
        const config = markerConfigs[item.level]
        if (!config) {
          console.warn(`路段 ${item.name} 缺少等级配置，level: ${item.level}`)
          return
        }
        
        try {
          const marker = createMarker(item.lng, item.lat, {
            iconUrl: config.iconUrl,
            iconSizeWidth: config.iconSizeWidth,
            iconSizeHeight: config.iconSizeHeight,
            iconAnchorX: config.iconAnchorX,
            iconAnchorY: config.iconAnchorY
          })
          
          if (marker) {
            console.log(`创建marker成功: ${item.name}`, { lng: item.lng, lat: item.lat, level: item.level })
            const added = addMarkerToMap(this.heatmapMap, marker)
            if (added) {
              markerCount++
              console.log(`添加marker成功: ${item.name} (${markerCount}/${validMapData.length})`)
            } else {
              console.error(`添加marker失败: ${item.name}`)
            }
            
            // 创建信息窗口内容
          const rankInfo = useRanking && item.rank ? `<div style="margin: 2px 0;">
            <span style="color: #909399;">排名：</span>
            <span style="color: #303133; font-weight: 600;">第${item.rank}名</span>
          </div>` : ''
          
          const content = `
            <div style="padding: 10px; min-width: 200px;">
              <div style="font-weight: 600; margin-bottom: 8px; font-size: 14px; color: #303133;">${item.name}</div>
              <div style="margin: 4px 0;">
                <span style="color: #909399;">${valueLabel}：</span>
                <span style="font-weight: 600; color: #303133;">${valueKey === 'rate' ? item.value + '%' : this.formatMoney(item.value)}</span>
              </div>
              ${rankInfo}
              <div style="margin: 4px 0; padding-top: 6px; border-top: 1px solid #ebeef5;">
                <div style="margin: 2px 0;">
                  <span style="color: #909399;">实收：</span>
                  <span style="color: #303133;">${this.formatMoney(item.real)}</span>
                  <span style="color: #909399; margin-left: 4px;">(${item.realPercent}%)</span>
                </div>
                <div style="margin: 2px 0;">
                  <span style="color: #909399;">应收：</span>
                  <span style="color: #303133;">${this.formatMoney(item.receivable)}</span>
                  <span style="color: #909399; margin-left: 4px;">(${item.receivablePercent}%)</span>
                </div>
                <div style="margin: 2px 0;">
                  <span style="color: #909399;">收费率：</span>
                  <span style="color: #303133;">${item.rate}%</span>
                </div>
              </div>
            </div>
          `
          
          // 创建信息窗口对象（兼容不同版本的API）
          let infoWindow = null
          try {
            const opts = {
              width: 250,
              height: 'auto'
            }
            
            // 尝试不同的InfoWindow创建方式
            if (T.InfoWindow) {
              infoWindow = new T.InfoWindow(opts)
              if (typeof infoWindow.setContent === 'function') {
                infoWindow.setContent(content)
              } else if (typeof infoWindow.setHTML === 'function') {
                infoWindow.setHTML(content)
              } else {
                opts.content = content
                infoWindow = new T.InfoWindow(opts)
              }
            }
          } catch (e) {
            console.warn('创建InfoWindow失败，尝试备用方式:', e)
            try {
              const opts = { content: content, width: 250, height: 'auto' }
              infoWindow = new T.InfoWindow(opts)
            } catch (e2) {
              console.error('创建InfoWindow完全失败:', e2)
            }
          }
          
          if (infoWindow) {
            // 绑定点击事件显示信息窗口
            marker.addEventListener('click', () => {
              const position = new T.LngLat(item.lng, item.lat)
              // 尝试不同的打开方式
              if (typeof this.heatmapMap.openInfoWindow === 'function') {
                this.heatmapMap.openInfoWindow(infoWindow, position)
              } else if (typeof marker.openInfoWindow === 'function') {
                marker.openInfoWindow(infoWindow)
              } else if (typeof infoWindow.open === 'function') {
                infoWindow.open(this.heatmapMap, position)
              }
            })
          }
          } else {
            console.warn(`创建marker失败: ${item.name}`, { lng: item.lng, lat: item.lat })
          }
        } catch (error) {
          console.error(`处理marker时出错: ${item.name}`, error)
        }
      })
      
      console.log(`总共添加了 ${markerCount} 个marker，有效数据: ${validMapData.length}，数据总数: ${mapData.length}`)
      
      // 验证添加的marker数量
      if (typeof T !== 'undefined' && T.Marker) {
        try {
          let allOverlay = []
          if (typeof this.heatmapMap.getOverlays === 'function') {
            allOverlay = this.heatmapMap.getOverlays()
          } else if (typeof this.heatmapMap.getLayers === 'function') {
            allOverlay = this.heatmapMap.getLayers()
          }
          const markerOverlays = allOverlay.filter(overlay => overlay instanceof T.Marker)
          console.log(`地图上实际有 ${markerOverlays.length} 个marker (期望: ${markerCount})`)
          if (markerOverlays.length !== markerCount) {
            console.warn('marker数量不匹配！可能存在问题')
          }
        } catch (e) {
          console.warn('检查marker数量失败:', e)
        }
      }
      
      // 如果有数据，调整地图视野以包含所有标记点
      if (mapData.length > 0 && mapData[0].lng) {
        const bounds = new T.LngLatBounds()
        mapData.forEach(item => {
          if (item.lng && item.lat) {
            bounds.extend(new T.LngLat(item.lng, item.lat))
          }
        })
        if (bounds.isValid()) {
          this.heatmapMap.setBounds(bounds)
        }
      }
    },
    
    getHeatmapData() {
      // 模拟数据，实际应从API获取，包含经纬度坐标
      return [
        { name: '锦程路', lng: 106.110698, lat: 30.837793, real: 9169, receivable: 49552, rate: 18.5 },
        { name: '锦亭路', lng: 106.112698, lat: 30.839793, real: 3892, receivable: 39431, rate: 9.87 },
        { name: '智达路', lng: 106.108698, lat: 30.835793, real: 8444, receivable: 34988, rate: 24.1 },
        { name: '清源北路', lng: 106.114698, lat: 30.841793, real: 7024, receivable: 28543, rate: 24.6 },
        { name: '镇江东路二段', lng: 106.106698, lat: 30.833793, real: 6543, receivable: 26789, rate: 24.4 },
        { name: '大兴街', lng: 106.116698, lat: 30.843793, real: 5234, receivable: 21543, rate: 24.3 },
        { name: '潆华西路', lng: 106.104698, lat: 30.831793, real: 3124, receivable: 31678, rate: 9.86 },
        { name: '潆华东路', lng: 106.102698, lat: 30.829793, real: 2891, receivable: 23456, rate: 12.3 },
        { name: '镇江东路一段', lng: 106.118698, lat: 30.845793, real: 4567, receivable: 25678, rate: 17.8 },
        { name: '清源南路', lng: 106.120698, lat: 30.847793, real: 3890, receivable: 19876, rate: 19.6 },
        { name: '锦华路', lng: 106.100698, lat: 30.827793, real: 3456, receivable: 18765, rate: 18.4 },
        { name: '智达南路', lng: 106.122698, lat: 30.849793, real: 4123, receivable: 22345, rate: 18.5 },
        { name: '清源中路', lng: 106.098698, lat: 30.825793, real: 3789, receivable: 20123, rate: 18.8 },
        { name: '锦程南路', lng: 106.124698, lat: 30.851793, real: 2987, receivable: 17654, rate: 16.9 },
        { name: '智达北路', lng: 106.096698, lat: 30.823793, real: 3456, receivable: 19876, rate: 17.4 }
      ]
    },
    
    // 对比分析图
    initComparisonChart() {
      const chartDom = document.getElementById('comparisonChart')
      if (!chartDom) return
      
      // 获取所有路段数据
      const allRoads = this.getHeatmapData()
      
      // 生成11月数据（基于12月数据计算，假设11月是12月的90%）
      const novReceivable = allRoads.map(item => Math.round(item.receivable * 0.9))
      const novReal = allRoads.map(item => Math.round(item.real * 0.9))
      
      // 提取路段名称和12月数据
      const roadNames = allRoads.map(item => item.name)
      const decReceivable = allRoads.map(item => item.receivable)
      const decReal = allRoads.map(item => item.real)
      const rates = allRoads.map(item => item.rate)
      
      const chart = this.$echarts.init(chartDom)
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' }
        },
        legend: {
          data: ['11月应收', '12月应收', '11月实收', '12月实收', '收费率'],
          bottom: 0
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          top: '10%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: roadNames,
          axisLabel: {
            rotate: 45, // 旋转45度
            fontSize: 10,
            interval: 0 // 显示所有标签
          }
        },
        yAxis: [
          {
            type: 'value',
            name: '金额（元）',
            axisLabel: { 
              formatter: function(value) {
                if (value >= 10000) {
                  return (value / 10000).toFixed(1) + '万'
                }
                return value
              }
            }
          },
          {
            type: 'value',
            name: '收费率（%）',
            axisLabel: { formatter: '{value}%' }
          }
        ],
        series: [
          {
            name: '11月应收',
            type: 'bar',
            data: novReceivable,
            itemStyle: { color: '#91CC75' }
          },
          {
            name: '12月应收',
            type: 'bar',
            data: decReceivable,
            itemStyle: { color: '#5470C6' }
          },
          {
            name: '11月实收',
            type: 'bar',
            data: novReal,
            itemStyle: { color: '#FAC858' }
          },
          {
            name: '12月实收',
            type: 'bar',
            data: decReal,
            itemStyle: { color: '#EE6666' }
          },
          {
            name: '收费率',
            type: 'line',
            yAxisIndex: 1,
            data: rates,
            itemStyle: { color: '#73C0DE' },
            lineStyle: { width: 2 }
          }
        ]
      }
      chart.setOption(option)
      this.charts.comparison = chart
      
      window.addEventListener('resize', () => chart.resize())
    },
    
    
    // 加载数据
    loadData() {
      // 这里应该调用API加载实际数据
      // this.$get(apiUrl).then(data => { ... })
    },
    
    // 初始化排行榜地图
    async initRankingMap() {
      const mapContainer = document.getElementById('rankingMap')
      if (!mapContainer) return
      
      try {
        // 等待天地图API加载完成
        await waitForTiandituAPI()
        
        // 创建天地图实例 - 定位到南充市
        const map = createTiandituMap('rankingMap', {
          centerLng: 106.110698, // 南充市经度
          centerLat: 30.837793,  // 南充市纬度
          zoom: 13
        })
        
        if (!map) {
          this.$message.error('地图初始化失败，请刷新页面重试')
          return
        }
        
        this.rankingMap = map
        
        // 设置地图事件
        setMapEvent(map)
        
        // 不添加地图控件（去掉缩放图标和logo）
        // addMapControl(map)
        
        // 初始化标记点
        this.updateRankingHeatmap()
        
        window.addEventListener('resize', () => {
          if (map && typeof map.resize === 'function') {
            map.resize()
          }
        })
      } catch (error) {
        console.error('初始化排行榜地图失败:', error)
        this.$message.error('地图初始化失败，请刷新页面重试')
      }
    },
    
    // 切换排行榜tab
    onRankingTabChange() {
      // 根据tab设置默认阈值
      if (this.activeRankingTab === 'spaceProductivity') {
        this.rankingGreenThreshold = '300' // 元/泊位
        this.rankingRedThreshold = '100' // 元/泊位
      } else if (this.activeRankingTab === 'turnover') {
        this.rankingGreenThreshold = '3' // 次/天
        this.rankingRedThreshold = '1' // 次/天
      }
      // 更新地图
      this.updateRankingHeatmap()
    },
    
    // 更新排行榜地图
    updateRankingHeatmap() {
      if (!this.rankingMap) return
      
      const data = this.getRankingData()
      
      const mapData = data.map(item => {
        const value = item.value
        let level = 'middle' // middle-橙色
        const greenThresholdNum = parseFloat(this.rankingGreenThreshold) || 0
        const redThresholdNum = parseFloat(this.rankingRedThreshold) || 0

        if (greenThresholdNum > 0 && value >= greenThresholdNum) {
          level = 'top' // top-绿色（大于等于绿色阈值）
        } else if (redThresholdNum > 0 && value <= redThresholdNum) {
          level = 'bottom' // bottom-红色（小于等于红色阈值）
        }

        return {
          ...item,
          value,
          level
        }
      })

      // 计算排名数据（前5名和后5名）
      const sortedData = [...mapData].sort((a, b) => b.value - a.value)
      this.totalCount2 = sortedData.length
      this.topRanking2 = sortedData.slice(0, 5).map(item => ({
        name: item.name,
        value: item.value
      }))
      this.bottomRanking2 = sortedData.slice(-5).map(item => ({
        name: item.name,
        value: item.value
      }))

      clearAllMarkers(this.rankingMap)

      const iconSize = 24
      const iconHeight = 30
      const iconAnchorX = iconSize / 2
      const iconAnchorY = iconHeight

      const markerConfigs = {
        top: { // 绿色
          iconUrl: this.createDropIcon('#67C23A', iconSize),
          iconSizeWidth: iconSize,
          iconSizeHeight: iconHeight,
          iconAnchorX: iconAnchorX,
          iconAnchorY: iconAnchorY,
          color: '#67C23A',
          label: '绿色'
        },
        middle: { // 橙色
          iconUrl: this.createDropIcon('#E6A23C', iconSize),
          iconSizeWidth: iconSize,
          iconSizeHeight: iconHeight,
          iconAnchorX: iconAnchorX,
          iconAnchorY: iconAnchorY,
          color: '#E6A23C',
          label: '其它'
        },
        bottom: { // 红色
          iconUrl: this.createDropIcon('#F56C6C', iconSize),
          iconSizeWidth: iconSize,
          iconSizeHeight: iconHeight,
          iconAnchorX: iconAnchorX,
          iconAnchorY: iconAnchorY,
          color: '#F56C6C',
          label: '红色'
        }
      }

      let markerCount = 0
      const validMapData = mapData.filter(item => item.lng && item.lat && item.level)
      console.log(`准备添加 ${validMapData.length} 个marker，总数据: ${mapData.length}`)

      validMapData.forEach((item, index) => {
        const config = markerConfigs[item.level]
        if (!config) {
          console.warn(`路段 ${item.name} 缺少等级配置，level: ${item.level}`)
          return
        }

        try {
          const marker = createMarker(item.lng, item.lat, {
            iconUrl: config.iconUrl,
            iconSizeWidth: config.iconSizeWidth,
            iconSizeHeight: config.iconSizeHeight,
            iconAnchorX: config.iconAnchorX,
            iconAnchorY: config.iconAnchorY
          })

          if (marker) {
            console.log(`创建marker成功: ${item.name}`, { lng: item.lng, lat: item.lat, level: item.level })
            const added = addMarkerToMap(this.rankingMap, marker)
            if (added) {
              markerCount++
              console.log(`添加marker成功: ${item.name} (${markerCount}/${validMapData.length})`)
            } else {
              console.error(`添加marker失败: ${item.name}`)
            }

            let content = ''
            if (this.activeRankingTab === 'spaceProductivity') {
              content = `
                <div style="padding: 10px; min-width: 200px;">
                  <div style="font-weight: 600; margin-bottom: 8px; font-size: 14px; color: #303133;">${item.name}</div>
                  <div style="margin: 4px 0;">
                    <span style="color: #909399;">实收/泊位：</span>
                    <span style="font-weight: 600; color: #303133;">${this.formatMoney(item.realPerSpace)}元/泊位</span>
                  </div>
                  <div style="margin: 4px 0;">
                    <span style="color: #909399;">应收/泊位：</span>
                    <span style="color: #303133;">${this.formatMoney(item.receivablePerSpace)}元/泊位</span>
                  </div>
                  <div style="margin: 4px 0;">
                    <span style="color: #909399;">泊位数：</span>
                    <span style="color: #303133;">${item.spaceCount || '--'}</span>
                  </div>
                </div>
              `
            } else {
              content = `
                <div style="padding: 10px; min-width: 200px;">
                  <div style="font-weight: 600; margin-bottom: 8px; font-size: 14px; color: #303133;">${item.name}</div>
                  <div style="margin: 4px 0;">
                    <span style="color: #909399;">周转率：</span>
                    <span style="font-weight: 600; color: #303133;">${item.value}次/天</span>
                  </div>
                </div>
              `
            }

            let infoWindow = null
            try {
              const opts = {
                width: 250,
                height: 'auto'
              }

              if (T.InfoWindow) {
                infoWindow = new T.InfoWindow(opts)
                if (typeof infoWindow.setContent === 'function') {
                  infoWindow.setContent(content)
                } else if (typeof infoWindow.setHTML === 'function') {
                  infoWindow.setHTML(content)
                } else {
                  opts.content = content
                  infoWindow = new T.InfoWindow(opts)
                }
              }
            } catch (e) {
              console.warn('创建InfoWindow失败，尝试备用方式:', e)
              try {
                const opts = { content: content, width: 250, height: 'auto' }
                infoWindow = new T.InfoWindow(opts)
              } catch (e2) {
                console.error('创建InfoWindow完全失败:', e2)
              }
            }

            if (infoWindow) {
              marker.addEventListener('click', () => {
                const position = new T.LngLat(item.lng, item.lat)
                if (typeof this.rankingMap.openInfoWindow === 'function') {
                  this.rankingMap.openInfoWindow(infoWindow, position)
                } else if (typeof marker.openInfoWindow === 'function') {
                  marker.openInfoWindow(infoWindow)
                } else if (typeof infoWindow.open === 'function') {
                  infoWindow.open(this.rankingMap, position)
                }
              })
            }
          }
        } catch (error) {
          console.error(`处理路段 ${item.name} 的marker时发生错误:`, error)
        }
      })

      if (mapData.length > 0 && mapData[0].lng) {
        const bounds = new T.LngLatBounds()
        mapData.forEach(item => {
          if (item.lng && item.lat) {
            bounds.extend(new T.LngLat(item.lng, item.lat))
          }
        })
        if (bounds.isValid()) {
          this.rankingMap.setBounds(bounds)
        }
      }
    },
    
    // 获取排行榜数据（包含经纬度）
    getRankingData() {
      if (this.activeRankingTab === 'spaceProductivity') {
        // 泊位单产数据 - 使用实收/泊位作为主要指标
        return this.spaceProductivityRanking.map(item => {
          // 根据路段名称匹配经纬度（复用热力图数据）
          const heatmapItem = this.getHeatmapData().find(h => h.name === item.roadName)
          return {
            name: item.roadName,
            lng: heatmapItem ? heatmapItem.lng : 106.110698 + (Math.random() - 0.5) * 0.02,
            lat: heatmapItem ? heatmapItem.lat : 30.837793 + (Math.random() - 0.5) * 0.02,
            value: item.realPerSpace, // 使用实收/泊位作为主要指标
            receivablePerSpace: item.receivablePerSpace,
            realPerSpace: item.realPerSpace,
            spaceCount: item.spaceCount
          }
        })
      } else {
        // 泊位周转率数据
        return this.turnoverRanking.map(item => {
          const heatmapItem = this.getHeatmapData().find(h => h.name === item.roadName)
          return {
            name: item.roadName,
            lng: heatmapItem ? heatmapItem.lng : 106.110698 + (Math.random() - 0.5) * 0.02,
            lat: heatmapItem ? heatmapItem.lat : 30.837793 + (Math.random() - 0.5) * 0.02,
            value: item.turnoverRate
          }
        })
      }
    },
    
    // 创建水滴图标（SVG转data URL）
    createDropIcon(color, size) {
      const darkColor = this.darkenColor(color, 20)
      const darkerColor = this.darkenColor(color, 30)
      const gradientId = `grad-${color.replace('#', '')}-${size}`
      const height = Math.round(size * 1.25)
      const halfSize = size / 2
      
      // 水滴形状的SVG路径 - 使用更简洁的路径
      const svg = `<svg width="${size}" height="${height}" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="${gradientId}" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" style="stop-color:${color};stop-opacity:1" />
      <stop offset="100%" style="stop-color:${darkColor};stop-opacity:1" />
    </linearGradient>
  </defs>
  <path d="M ${halfSize} ${size * 0.15} 
           C ${size * 0.25} ${size * 0.3}, ${size * 0.2} ${size * 0.5}, ${size * 0.25} ${size * 0.7}
           C ${size * 0.3} ${size * 0.9}, ${size * 0.4} ${size * 1.05}, ${halfSize} ${height * 0.95}
           C ${size * 0.6} ${size * 1.05}, ${size * 0.7} ${size * 0.9}, ${size * 0.75} ${size * 0.7}
           C ${size * 0.8} ${size * 0.5}, ${size * 0.75} ${size * 0.3}, ${halfSize} ${size * 0.15} Z" 
        fill="url(#${gradientId})" 
        stroke="${darkerColor}" 
        stroke-width="1.2"
        opacity="0.95"/>
  <ellipse cx="${halfSize}" cy="${size * 0.4}" rx="${size * 0.12}" ry="${size * 0.08}" fill="rgba(255,255,255,0.4)"/>
</svg>`
      
      try {
        return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)))
      } catch (e) {
        console.error('生成水滴图标失败:', e)
        // 如果base64编码失败，直接返回SVG data URL
        return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg)
      }
    },
    
    // 加深颜色
    darkenColor(color, percent) {
      const num = parseInt(color.replace('#', ''), 16)
      const r = Math.max(0, Math.floor((num >> 16) * (1 - percent / 100)))
      const g = Math.max(0, Math.floor(((num >> 8) & 0x00FF) * (1 - percent / 100)))
      const b = Math.max(0, Math.floor((num & 0x0000FF) * (1 - percent / 100)))
      return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')
    },
    
    // 格式化金额
    formatMoney(value) {
      if (!value) return '0'
      return Number(value).toLocaleString('zh-CN')
    },
    
    // 格式化排名值（根据维度显示不同格式）
    formatRankingValue(value) {
      if (this.heatmapDimension === 'rate') {
        return `${value}%`
      } else {
        return `${this.formatMoney(value)}元`
      }
    },
    
    // 格式化排名值2（排行榜用）
    formatRankingValue2(value) {
      if (this.activeRankingTab === 'turnover') {
        return `${value}次/天`
      } else {
        return `${this.formatMoney(value)}元/泊位`
      }
    },
    
    
    // 跳转到路段详情
    goToRoadDetail(roadName) {
      // 跳转逻辑
      console.log('跳转到路段详情:', roadName)
    },
    
    // 计算预警数据
    calculateWarnings() {
      const warnings = []
      const heatmapData = this.getHeatmapData()
      const turnoverData = this.turnoverRanking
      
      // 检查低收费率路段
      const lowRateRoads = heatmapData
        .filter(item => item.rate < this.warningThresholds.lowRate)
        .map(item => `${item.name}(${item.rate}%)`)
      
      if (lowRateRoads.length > 0) {
        warnings.push({
          type: 'low-rate',
          icon: '🔴',
          text: `低收费率路段：${lowRateRoads.join('、')}`
        })
      }
      
      // 检查低周转路段
      const lowTurnoverRoads = turnoverData
        .filter(item => item.turnoverRate < this.warningThresholds.lowTurnover)
        .map(item => `${item.roadName}(${item.turnoverRate}次/天)`)
      
      if (lowTurnoverRoads.length > 0) {
        warnings.push({
          type: 'low-turnover',
          icon: '🟡',
          text: `低周转路段：${lowTurnoverRoads.join('、')}`
        })
      }
      
      // 检查高欠费路段（收费率低于某个值视为高欠费）
      const highArrearsRoads = heatmapData
        .filter(item => {
          const arrearsRate = 100 - item.rate
          return arrearsRate > this.warningThresholds.highArrears
        })
        .map(item => `${item.name}(欠费率${(100 - item.rate).toFixed(1)}%)`)
      
      if (highArrearsRoads.length > 0) {
        warnings.push({
          type: 'high-arrears',
          icon: '🔵',
          text: `高欠费路段：${highArrearsRoads.join('、')}`
        })
      }
      
      this.warnings = warnings
    },
    
    // 保存预警阈值
    saveWarningThresholds() {
      this.calculateWarnings()
      this.showWarningSettings = false
      this.$message.success('预警阈值设置已保存')
    }
  },
  
  beforeDestroy() {
    // 销毁所有图表实例
    Object.values(this.charts).forEach(chart => {
      if (chart && chart.dispose) {
        chart.dispose()
      }
    })
    
    // 清理地图实例
    if (this.heatmapMap) {
      try {
        // 清除所有标记点
        clearAllMarkers(this.heatmapMap)
        // 销毁地图实例
        if (typeof this.heatmapMap.destroy === 'function') {
          this.heatmapMap.destroy()
        }
        this.heatmapMap = null
      } catch (error) {
        console.warn('清理地图实例失败:', error)
      }
    }
    if (this.rankingMap) {
      try {
        // 清除所有标记点
        clearAllMarkers(this.rankingMap)
        // 销毁地图实例
        if (typeof this.rankingMap.destroy === 'function') {
          this.rankingMap.destroy()
        }
        this.rankingMap = null
      } catch (error) {
        console.warn('清理排行榜地图实例失败:', error)
      }
    }
  }
}
</script>

<style scoped>
.data-overview {
  padding: 15px;
  background: #f5f7fa;
  min-height: 100vh;
}

/* 顶部指标卡 */
.top-indicators {
  margin-bottom: 12px;
}

.indicator-cards {
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
}

.indicator-card {
  flex: 1;
  background: #fff;
  border-radius: 6px;
  padding: 12px 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #ebeef5;
}

.card-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.card-period {
  font-size: 11px;
  color: #909399;
}

.card-content {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px 12px;
}

.card-content.income-overview {
  grid-template-columns: repeat(3, 1fr);
  gap: 8px 10px;
}

.card-content.efficiency-overview {
  grid-template-columns: repeat(2, 1fr);
  gap: 8px 12px;
}

.indicator-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  padding: 8px 10px;
  background: #f8f9fa;
  border-radius: 4px;
  transition: all 0.3s;
}

.indicator-item:hover {
  background: #f0f2f5;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.indicator-item .label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 2px;
  line-height: 1.2;
}

.value-wrapper {
  display: flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
}

.indicator-item .value {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  line-height: 1.3;
}

.indicator-item .unit {
  font-size: 12px;
  color: #909399;
}

.indicator-item .trend {
  font-size: 10px;
  padding: 1px 4px;
  border-radius: 3px;
  align-self: flex-start;
  line-height: 1.4;
}

.trend.up {
  color: #67C23A;
  background: #f0f9ff;
}

.trend.down {
  color: #F56C6C;
  background: #fef0f0;
}

.trend-cards {
  display: flex;
  gap: 20px;
}

.trend-card {
  flex: 1;
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.trend-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  font-size: 14px;
  color: #606266;
}

.current-value {
  font-size: 18px;
  font-weight: 600;
  color: #409EFF;
}

.trend-chart {
  height: 150px;
}

/* 数据看板主区域 */
.dashboard-main {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.dashboard-row {
  display: flex;
  gap: 20px;
  flex: 1;
}

.dashboard-row .dashboard-quadrant {
  flex: 1;
  min-width: 0; /* 允许flex子项缩小 */
}

.dashboard-grid {
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 20px;
}

.dashboard-quadrant {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.quadrant-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}

.quadrant-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.heatmap-controls {
  display: flex;
  gap: 10px;
}

.heatmap-container {
  height: 450px;
  min-height: 400px;
  position: relative;
}

/* 隐藏天地图logo和版权信息 */
.heatmap-container :deep(.tdt-control-logo),
.heatmap-container :deep(.tdt-control-copyright),
.heatmap-container :deep(.tdt-logo),
.heatmap-container :deep(.tdt-copyright),
.heatmap-container :deep(.TControl_Logo),
.heatmap-container :deep(.TControl_Copyright) {
  display: none !important;
}

/* 排名浮动面板 */
.ranking-floating-panel {
  position: absolute;
  top: 0px;
  left: 0px;
  width: 200px;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 6px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  overflow: hidden;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.9);
  color: #303133;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.panel-title {
  font-size: 12px;
  font-weight: 600;
}

.close-btn {
  color: #606266;
  padding: 0;
  font-size: 14px;
}

.close-btn:hover {
  color: #303133;
}

.panel-content {
  padding: 5px;
  /* max-height: 320px; */
  overflow-y: auto;
}

.ranking-section {
  margin-bottom: 12px;
}

.ranking-section:last-child {
  margin-bottom: 0;
}

.section-title {
  font-size: 11px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 6px;
  padding-bottom: 4px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.ranking-floating-panel .ranking-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ranking-floating-panel .ranking-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px;
  border-radius: 3px;
  transition: all 0.3s;
  cursor: pointer;
}

.ranking-floating-panel .ranking-item:hover {
  background: rgba(0, 0, 0, 0.05);
  transform: translateX(2px);
}

.ranking-floating-panel .ranking-item.top-item {
  background: rgba(103, 194, 58, 0.1);
  border-left: 2px solid #67C23A;
}

.ranking-floating-panel .ranking-item.bottom-item {
  background: rgba(245, 108, 108, 0.1);
  border-left: 2px solid #F56C6C;
}

.rank-number {
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 10px;
  font-weight: 600;
  flex-shrink: 0;
}

.top-item .rank-number {
  background: #67C23A;
  color: #fff;
}

.bottom-item .rank-number {
  background: #F56C6C;
  color: #fff;
}

.road-name {
  flex: 1;
  font-size: 11px;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.road-value {
  font-size: 10px;
  font-weight: 600;
  color: #606266;
  flex-shrink: 0;
}

.top-item .road-value {
  color: #67C23A;
}

.bottom-item .road-value {
  color: #F56C6C;
}

/* 显示面板按钮 */
.show-panel-btn {
  position: absolute;
  top: 10px;
  left: 10px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 6px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  z-index: 999;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #303133;
  transition: all 0.3s;
}

.show-panel-btn:hover {
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  transform: translateY(-1px);
}

.show-panel-btn i {
  font-size: 14px;
}

/* 地图图例说明 - 地图上方 */
.heatmap-legend-header {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 10px 15px;
  margin-bottom: 10px;
  background: #f5f7fa;
  border-radius: 4px;
  font-size: 12px;
}

.heatmap-legend-header .legend-title {
  color: #606266;
  font-weight: 500;
  white-space: nowrap;
}

.heatmap-legend-header .legend-items {
  display: flex;
  gap: 20px;
  align-items: center;
}

.heatmap-legend-header .legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.heatmap-legend-header .legend-icon {
  width: 20px;
  height: 25px;
  object-fit: contain;
}

/* 阈值控制区域 - 独立一行 */
.threshold-controls-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 15px;
  margin-bottom: 10px;
  background: #f5f7fa;
  border-radius: 4px;
  font-size: 12px;
}

.threshold-controls-header .threshold-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.threshold-controls-header .threshold-item label {
  font-size: 12px;
  color: #606266;
  white-space: nowrap;
}

.threshold-controls-header .threshold-unit {
  font-size: 12px;
  color: #909399;
}

.threshold-controls-header .confirm-btn {
  width:40px;
  display:flex;
  justify-content: center;
 
}

.heatmap-legend-header .legend-label {
  color: #303133;
  font-size: 12px;
}

/* 排行榜 */
.ranking-tabs {
  margin-top: 15px;
}

.ranking-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ranking-item {
  display: flex;
  align-items: center;
  padding: 10px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.3s;
}

.ranking-item:hover {
  background: #f5f7fa;
}

.ranking-item .rank {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #409EFF;
  color: #fff;
  border-radius: 50%;
  font-weight: 600;
  margin-right: 10px;
}

.ranking-item .name {
  flex: 1;
  font-size: 14px;
  color: #303133;
}

.ranking-item .value {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-right: 10px;
}

.ranking-item .percent {
  font-size: 12px;
  color: #909399;
}

.productivity-values {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  margin-left: 10px;
}

.productivity-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
}

.productivity-item .label {
  color: #909399;
  min-width: 40px;
}

.productivity-item .value {
  font-weight: 600;
  color: #303133;
}

.productivity-item .unit {
  font-size: 11px;
  color: #909399;
}

.ranking-item.warning {
  background: #fef0f0;
}

.rate-ranking {
  display: flex;
  gap: 20px;
}

.rate-section {
  flex: 1;
}

.rate-section h4 {
  margin: 0 0 10px 0;
  font-size: 14px;
  color: #606266;
}

/* 对比分析 */
.comparison-chart {
  height: 400px;
  margin-bottom: 20px;
}

.top3-container {
  display: flex;
  gap: 30px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
}

.growth-top3 {
  flex: 1;
}

.growth-top3 h4 {
  margin: 0 0 10px 0;
  font-size: 14px;
  color: #606266;
}

.growth-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.growth-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  background: #f5f7fa;
  border-radius: 4px;
}

.growth-item .name {
  flex: 1;
  font-size: 14px;
  color: #303133;
}

.growth-item .ratio {
  font-size: 14px;
  font-weight: 600;
  color: #67C23A;
}

.growth-item .trend-icon {
  font-size: 16px;
}

/* 绩效与周转 */
.performance-section {
  margin-bottom: 0;
  min-height: 600px;
}

/* 收费员绩效表格 */
.charge-user-table {
  width: 100%;
}

.charge-user-table :deep(.el-table__header) {
  background: #f5f7fa;
}

.charge-user-table :deep(.el-table__header th) {
  background: #f5f7fa;
  color: #606266;
  font-weight: 600;
  font-size: 13px;
  padding: 10px 0;
}

.charge-user-table :deep(.el-table__body td) {
  padding: 12px 0;
}

.charge-user-table .user-name {
  font-weight: 500;
  color: #303133;
  font-size: 14px;
}

.charge-user-table .performance-cell {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.charge-user-table .main-value {
  color: #303133;
  font-weight: 600;
  font-size: 14px;
}

.charge-user-table .unit {
  color: #909399;
  font-size: 12px;
}

.charge-user-table .percent {
  color: #409EFF;
  font-weight: 500;
  font-size: 13px;
}

/* 预警相关样式 */
.warning-item {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.warning-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  width: 100%;
}

.warning-tag.low-rate {
  background: #fef0f0;
  color: #F56C6C;
}

.warning-tag.low-turnover {
  background: #fdf6ec;
  color: #E6A23C;
}

.warning-tag.high-arrears {
  background: #ecf5ff;
  color: #409EFF;
}

.warning-tag .warning-icon {
  font-size: 12px;
}

.warning-tag .warning-text {
  color: inherit;
}

.no-warning {
  color: #909399;
  font-size: 12px;
}

.warning-settings-btn {
  margin-left: 8px;
  padding: 0;
  font-size: 12px;
  color: #409EFF;
}

.warning-settings-btn:hover {
  color: #66b1ff;
}

/* 预警阈值设置表单 */
.warning-threshold-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 10px 0;
}

.threshold-form-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.threshold-form-item label {
  width: 120px;
  font-size: 14px;
  color: #606266;
  text-align: right;
}

.threshold-form-item .unit {
  font-size: 14px;
  color: #909399;
  margin-left: 5px;
}

.threshold-form-item .desc {
  font-size: 12px;
  color: #909399;
  margin-left: 10px;
}

/* 顶部统计栏 */
.top-stats {
  display: flex;
  justify-content: space-around;
  align-items: center;
  margin-bottom: 12px;
  padding: 10px 15px;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
}

.stat-item .label {
  color: #909399;
}

.stat-item .value {
  color: #303133;
  font-weight: 500;
}

/* 智能预警统计栏 */
.warning-stats {
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
}

.warning-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.warning-list-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.warning-row {
  display: flex;
  align-items: center;
  width: 100%;
}

/* 响应式设计 */
@media (max-width: 1400px) {
  .dashboard-grid {
    grid-template-columns: 1fr;
  }
  
}

@media (max-width: 768px) {
  .indicator-cards,
  .trend-cards {
    flex-direction: column;
  }
  
  .card-content {
    grid-template-columns: 1fr;
    gap: 6px 10px;
  }
  
  .card-content.income-overview {
    grid-template-columns: 1fr;
  }
  
  .indicator-item {
    padding: 6px 8px;
  }
  
  .indicator-item .value {
    font-size: 16px;
  }
  
  .indicator-card {
    padding: 10px 12px;
  }
  
  .dashboard-main {
    flex-direction: column;
  }
  
  .dashboard-row {
    flex-direction: column;
  }
  
  .dashboard-row .dashboard-quadrant {
    width: 100%;
  }
  
  .sidebar {
    width: 100%;
  }
  
  .top-stats {
    flex-direction: column;
    gap: 10px;
  }
  
  .top3-container {
    flex-direction: column;
    gap: 20px;
  }
}
</style>
